import type { StateCreator } from 'zustand'
import { logger } from '@/shared/lib/logger'
import type { Workspace } from '../../types'
import {
  fetchWorkspaces,
  createWorkspaceApi,
  updateWorkspaceApi,
  activateWorkspaceApi,
  deleteWorkspaceApi,
  type WorkspacePayload,
} from '@/shared/lib/api'

export interface WorkspaceSlice {
  workspaces: Workspace[]
  activeWorkspace: Workspace | null
  workspacesLoading: boolean

  fetchWorkspaces: () => Promise<void>
  switchWorkspace: (id: number) => Promise<void>
  addWorkspace: (data: WorkspacePayload) => Promise<void>
  editWorkspace: (id: number, data: Partial<WorkspacePayload>) => Promise<void>
  removeWorkspace: (id: number) => Promise<void>
}

export const createWorkspaceSlice: StateCreator<
  WorkspaceSlice,
  [],
  [],
  WorkspaceSlice
> = (set) => ({
  workspaces: [],
  activeWorkspace: null,
  workspacesLoading: false,

  fetchWorkspaces: async () => {
    set({ workspacesLoading: true })
    try {
      const list = await fetchWorkspaces()
      const active = list.find((w) => w.is_active) ?? null
      set({ workspaces: list, activeWorkspace: active })
    } catch (err) {
      logger.error('Failed to fetch workspaces', { error: err })
    } finally {
      set({ workspacesLoading: false })
    }
  },

  switchWorkspace: async (id: number) => {
    try {
      const activated = await activateWorkspaceApi(id)
      // Re-fetch full list so is_active flags are correct
      const list = await fetchWorkspaces()
      set({ workspaces: list, activeWorkspace: activated })
    } catch (err) {
      logger.error('Failed to switch workspace', { error: err })
    }
  },

  addWorkspace: async (data: WorkspacePayload) => {
    const created = await createWorkspaceApi(data)
    set((s) => ({ workspaces: [...s.workspaces, created] }))
  },

  editWorkspace: async (id: number, data: Partial<WorkspacePayload>) => {
    const updated = await updateWorkspaceApi(id, data)
    set((s) => ({
      workspaces: s.workspaces.map((w) => (w.id === id ? updated : w)),
      activeWorkspace: s.activeWorkspace?.id === id ? updated : s.activeWorkspace,
    }))
  },

  removeWorkspace: async (id: number) => {
    await deleteWorkspaceApi(id)
    set((s) => ({
      workspaces: s.workspaces.filter((w) => w.id !== id),
    }))
  },
})
